import { useQueries } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCan } from "components/Can";
import { useTypedQuery } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { LocationReportQueryKeys } from "modules/LocationReports/queryKeys";
import { ApiInstance } from "utils/Api";
import { translateReportForCharts } from "utils/common";

import type { QueryFunctionContext } from "@tanstack/react-query";
import type { ParsedLocationReportData } from "modules/LocationReports";
import type { IBackendQueryKey } from "utils/keyFactory";

type UseLocationReportsParams = {
  dealer_location_id: number;
  date_to?: string;
  date_from?: string;
};

export const useLocationReports = ({ dealer_location_id, date_from, date_to }: UseLocationReportsParams) => {
  const { t } = useTranslation();

  const canListLocationsReport = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.LocationsReport);
  const canListLocationsReportGroup = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.LocationsReportGroup);

  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [currentReportIndex, setCurrentReportIndex] = useState<number>(-1);

  const initialGroupSetRef = useRef(false);
  const lastValidDatesRef = useRef<{ date_from: string; date_to: string } | null>(null);

  if (date_from && date_to) lastValidDatesRef.current = { date_from, date_to };
  const datesForQuery = lastValidDatesRef.current;

  const reportList = useTypedQuery({
    queryKey: LocationReportQueryKeys.listReports(dealer_location_id, Number(selectedGroup)),
    queryFn: async ({ endpoint, baseUrl, params }) => {
      try {
        return (await (await ApiInstance.post(endpoint, { ...params }, baseUrl)).data?.template_ids) ?? [];
      } catch (err) {
        toast.error(t("v8_failed_to_load_report", "Failed to load report"));
        throw err;
      }
    },
    enabled: canListLocationsReport && Boolean(selectedGroup && dealer_location_id),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const getLocationReport = async ({ signal, queryKey }: QueryFunctionContext<ReadonlyArray<IBackendQueryKey>>, index: number) => {
    const { baseUrl, params, endpoint } = queryKey[0];
    try {
      const response = await ApiInstance.post(endpoint, params, baseUrl, { signal });
      const report = response.data;
      const parsedReportData = report.report_data ? report.report_data.map((d: string) => JSON.parse(d)) : [];
      const parsedReportThreshold = report.thresholds ? JSON.parse(report.thresholds) : [];

      const translatedData = parsedReportData.map((d: string[]) => translateReportForCharts(d, t));

      if (reportList.data && index < reportList.data.length - 1) setCurrentReportIndex(index);

      return { ...response.data, report_data: translatedData, thresholds: parsedReportThreshold } as ParsedLocationReportData;
    } catch (err) {
      toast.error(t("v8_failed_to_load_report", "Failed to load report"));
      throw err;
    }
  };

  const groups = useTypedQuery({
    queryKey: LocationReportQueryKeys.listGroups(dealer_location_id),
    queryFn: async ({ endpoint, baseUrl, params }) => {
      try {
        const response = await ApiInstance.post(endpoint, { ...params }, baseUrl);
        setSelectedGroup(response.data.groups[0]);
        return response.data.groups ?? [];
      } catch (err) {
        toast.error(t("v8_failed_to_load_report_groups", "Failed to load report groups"));
        throw err;
      }
    },
    enabled: !!dealer_location_id && canListLocationsReportGroup,
    staleTime: Infinity
  });

  useEffect(() => {
    if (groups.data && groups.data.length > 0 && !selectedGroup && !initialGroupSetRef.current) {
      initialGroupSetRef.current = true;
      setSelectedGroup(groups.data[0]);
    }
  }, [groups.data, selectedGroup]);

  useEffect(() => {
    setCurrentReportIndex(-1);
  }, [datesForQuery?.date_from, datesForQuery?.date_to, selectedGroup]);

  const reports = useQueries({
    queries:
      reportList.data?.map((report_id: number, index: number) => ({
        queryKey: LocationReportQueryKeys.locationReport(index, dealer_location_id, report_id, datesForQuery?.date_from, datesForQuery?.date_to),
        queryFn: (queryFnParams: QueryFunctionContext<ReadonlyArray<IBackendQueryKey>>) => getLocationReport(queryFnParams, index),
        enabled: canListLocationsReport && !!report_id && !!datesForQuery && (index === 0 || index === currentReportIndex + 1)
      })) ?? []
  });

  return {
    groups,
    selectedGroup,
    setSelectedGroup,
    reportList,
    reports,
    loading: groups.isLoading || reportList.isLoading,
    error: groups.isError || reportList.isError
  };
};
